import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { contentAPI } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  BookOpen, FileText, Video, Headphones, 
  Lock, Briefcase, Heart, Sparkles,
  ChevronRight, Clock, X, CheckCircle2, XCircle, CircleDot, ClipboardList, Pencil,
  Volume2, Loader2, Pause, Play, GripVertical, Plus, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function AssessmentWidget({ contentId, questions, assessmentType, giftTypes }) {
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const allAnswered = Object.keys(answers).length === questions.length;
  const isTypeQuiz = assessmentType === 'type_quiz';

  const handleSelect = (qIndex, cIndex) => {
    if (results) return;
    setAnswers(prev => ({ ...prev, [qIndex]: cIndex }));
  };

  const handleSubmit = async () => {
    const orderedAnswers = questions.map((_, i) => answers[i] ?? -1);
    setSubmitting(true);
    try {
      const response = await contentAPI.submitAssessment(contentId, orderedAnswers);
      setResults(response.data);
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const getResultForQuestion = (qIndex) => {
    return results?.results?.find(r => r.question_index === qIndex);
  };

  // Type Quiz Results Display
  if (results && isTypeQuiz) {
    const dominant = results.dominant_types || [];
    const topType = dominant[0];
    const secondType = dominant.length > 1 ? dominant[1] : null;
    
    return (
      <div className="mt-8 border-t border-border pt-6" data-testid="type-quiz-results">
        <h3 className="text-lg font-serif mb-6 text-center">Your Spiritual Gift Results</h3>
        
        {topType && (
          <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-2xl p-6 mb-4 border border-accent/20" data-testid="primary-type-result">
            <p className="text-xs font-medium text-accent uppercase tracking-wider mb-2">Your Dominant Gift</p>
            <h4 className="text-2xl font-serif mb-3">{topType.name}</h4>
            <p className="text-muted-foreground mb-4">{topType.description}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-card rounded-lg p-3">
                <p className="text-xs font-medium text-green-600 mb-1">Strength</p>
                <p className="text-sm">{topType.strength}</p>
              </div>
              <div className="bg-card rounded-lg p-3">
                <p className="text-xs font-medium text-amber-600 mb-1">Growth Edge</p>
                <p className="text-sm">{topType.struggle}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">{topType.count} of {questions.length} answers</p>
          </div>
        )}

        {secondType && secondType.count > 1 && (
          <div className="bg-muted/30 rounded-xl p-5 mb-4 border border-border" data-testid="secondary-type-result">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Your Secondary Gift</p>
            <h4 className="text-lg font-serif mb-2">{secondType.name}</h4>
            <p className="text-sm text-muted-foreground mb-3">{secondType.description}</p>
            <div className="flex gap-4 text-xs">
              <span><span className="font-medium text-green-600">Strength:</span> {secondType.strength}</span>
              <span><span className="font-medium text-amber-600">Growth Edge:</span> {secondType.struggle}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{secondType.count} of {questions.length} answers</p>
          </div>
        )}

        {/* All types breakdown */}
        <div className="mt-4">
          <p className="text-sm font-medium mb-3">Full Breakdown</p>
          <div className="space-y-2">
            {(giftTypes || []).map((type) => {
              const count = results.type_counts?.[type.id] || 0;
              const percentage = Math.round((count / questions.length) * 100);
              return (
                <div key={type.id} className="flex items-center gap-3" data-testid={`type-bar-${type.id}`}>
                  <span className="text-xs w-32 truncate">{type.name}</span>
                  <div className="flex-1 bg-muted rounded-full h-2.5">
                    <div 
                      className="bg-accent rounded-full h-2.5 transition-all duration-500" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-8">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-accent/5 border border-accent/10">
          <p className="text-sm text-muted-foreground italic">
            Remember — this isn't a label. It's a starting point for understanding how you naturally connect, process, and lead. You likely carry pieces of multiple gifts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 border-t border-border pt-6" data-testid="assessment-section">
      <h3 className="text-lg font-serif mb-2 flex items-center gap-2">
        <CircleDot size={20} className="text-accent" />
        {isTypeQuiz ? 'Discover Your Type' : 'Check Your Understanding'}
      </h3>
      {isTypeQuiz && (
        <p className="text-sm text-muted-foreground mb-6">Answer honestly — not what you think you should say, but what actually feels true. There are no wrong answers.</p>
      )}

      <div className="space-y-6">
        {questions.map((q, qi) => {
          const result = getResultForQuestion(qi);
          return (
            <div key={q.question_text || `q-${qi}`} className="space-y-3" data-testid={`assessment-q-${qi}`}>
              <p className="font-medium text-sm">
                {qi + 1}. {q.question_text}
                {!isTypeQuiz && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    q.type === 'knowledge_check' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {q.type === 'knowledge_check' ? 'Knowledge' : 'Reflection'}
                  </span>
                )}
              </p>
              <div className="space-y-2">
                {q.choices?.map((choice, ci) => {
                  const isSelected = answers[qi] === ci;
                  const showCorrect = result && q.type === 'knowledge_check';
                  let borderClass = 'border-border hover:border-primary/40';
                  if (isSelected && !results) borderClass = 'border-primary bg-primary/5';
                  if (showCorrect && choice.is_correct) borderClass = 'border-green-500 bg-green-50';
                  if (showCorrect && isSelected && !choice.is_correct) borderClass = 'border-red-300 bg-red-50';
                  if (results && !showCorrect && isSelected) borderClass = 'border-accent bg-accent/5';

                  return (
                    <button
                      key={ci}
                      onClick={() => handleSelect(qi, ci)}
                      disabled={!!results}
                      className={`w-full text-left p-3 rounded-lg border ${borderClass} transition-all text-sm flex items-start gap-3`}
                      data-testid={`assessment-q${qi}-c${ci}`}
                    >
                      <span className="flex-shrink-0 mt-0.5">
                        {showCorrect && choice.is_correct ? (
                          <CheckCircle2 size={16} className="text-green-500" />
                        ) : showCorrect && isSelected && !choice.is_correct ? (
                          <XCircle size={16} className="text-red-400" />
                        ) : (
                          <div className={`w-4 h-4 rounded-full border-2 ${isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40'}`} />
                        )}
                      </span>
                      <div>
                        <span>{choice.text}</span>
                        {results && !isTypeQuiz && isSelected && choice.feedback && (
                          <p className="text-xs text-muted-foreground mt-1 italic">{choice.feedback}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {result?.explanation && result.type === 'knowledge_check' && (
                <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">{result.explanation}</p>
              )}
            </div>
          );
        })}
      </div>

      {!results ? (
        <Button
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
          className="mt-6 rounded-full"
          data-testid="submit-assessment-btn"
        >
          {submitting ? 'Discovering...' : isTypeQuiz ? `Discover My Gift (${Object.keys(answers).length}/${questions.length})` : `Submit Answers (${Object.keys(answers).length}/${questions.length})`}
        </Button>
      ) : !isTypeQuiz ? (
        <div className="mt-6 p-4 rounded-lg bg-accent/10 border border-accent/20" data-testid="assessment-results">
          <p className="font-medium flex items-center gap-2">
            <CheckCircle2 size={18} className="text-accent" />
            Assessment Complete
          </p>
          {results.score && (
            <p className="text-sm text-muted-foreground mt-1">Knowledge check score: {results.score}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/,
    /(?:vimeo\.com\/)([\d]+)/
  ];
  const ytMatch = url.match(patterns[0]);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(patterns[1]);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}


const PATH_INFO = {
  spiritual: {
    name: "Spiritual Path",
    description: "Nurture your soul and connect with your deeper purpose",
    icon: Sparkles,
    color: "from-purple-500/20 to-violet-500/20",
    borderColor: "border-purple-200"
  },
  personal: {
    name: "Personal Path",
    description: "Build resilience and create the life you want",
    icon: Heart,
    color: "from-pink-500/20 to-rose-500/20",
    borderColor: "border-pink-200"
  },
  professional: {
    name: "Professional Path",
    description: "Advance your career and achieve professional goals",
    icon: Briefcase,
    color: "from-blue-500/20 to-cyan-500/20",
    borderColor: "border-blue-200"
  }
};

const TYPE_ICONS = {
  article: FileText,
  worksheet: BookOpen,
  video: Video,
  audio: Headphones,
  'self-assessment': ClipboardList
};

const TYPE_LABELS = {
  article: "Article",
  worksheet: "Worksheet",
  video: "Video",
  audio: "Audio",
  'self-assessment': "Self-Assessment"
};

export default function ContentLibraryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [topics, setTopics] = useState({});
  const [paths, setPaths] = useState({});
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(searchParams.get('topic') || null);
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || null);
  const [selectedPath, setSelectedPath] = useState(searchParams.get('path') || null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [showAddContent, setShowAddContent] = useState(false);
  const [newContent, setNewContent] = useState({
    title: '', description: '', content_type: 'article', path_category: 'personal',
    topic: '', content_body: '', content_url: '', duration_minutes: '', is_featured: false
  });
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const audioRef = useRef(null);
  const [reorderingPath, setReorderingPath] = useState(null);
  const [reorderItems, setReorderItems] = useState([]);
  const [reorderSaving, setReorderSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2];

  // Safety net: stop audio whenever dialog closes
  useEffect(() => {
    if (!selectedContent && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, [selectedContent]);

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const handleListen = async (contentId) => {
    if (audioUrl) {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
      return;
    }

    setAudioLoading(true);
    try {
      const response = await contentAPI.generateTTS(contentId);
      const blob = new Blob([response.data], { type: 'audio/mp3' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      
      const audio = new Audio(url);
      audio.playbackRate = playbackSpeed;
      audioRef.current = audio;
      audio.onended = () => setIsPlaying(false);
      audio.play();
      setIsPlaying(true);
    } catch (error) {
      toast.error('Failed to generate audio');
    } finally {
      setAudioLoading(false);
    }
  };

  // Cleanup audio on dialog close
  const handleCloseContent = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setIsPlaying(false);
    setPlaybackSpeed(1);
    setSelectedContent(null);
  };

  const startReorder = (pathKey) => {
    const pathContent = contentByPath[pathKey] || [];
    setReorderItems([...pathContent]);
    setReorderingPath(pathKey);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setReorderItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveReorder = async () => {
    setReorderSaving(true);
    try {
      const orderUpdates = reorderItems.map((item, index) => ({ id: item.id, order: index }));
      await contentAPI.reorderContent(orderUpdates);
      toast.success('Content order saved');
      setReorderingPath(null);
      setReorderItems([]);
      loadContent();
    } catch (error) {
      toast.error('Failed to save order');
    } finally {
      setReorderSaving(false);
    }
  };

  const handleMarkComplete = async (contentId, currentlyCompleted) => {
    setMarkingComplete(true);
    try {
      await contentAPI.updateProgress(contentId, { completed: !currentlyCompleted });
      setSelectedContent(prev => prev ? { ...prev, completed: !currentlyCompleted } : prev);
      loadContent();
      toast.success(!currentlyCompleted ? 'Lesson marked as complete!' : 'Marked as incomplete');
    } catch (error) {
      toast.error('Failed to update progress');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleAddContent = async () => {
    if (!newContent.title || !newContent.description) {
      toast.error('Title and description are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: newContent.title,
        description: newContent.description,
        content_type: newContent.content_type,
        path_category: newContent.path_category,
        topic: newContent.topic || 'general',
        content_body: newContent.content_body || null,
        content_url: newContent.content_url || null,
        duration_minutes: newContent.duration_minutes ? parseInt(newContent.duration_minutes) : null,
        is_featured: newContent.is_featured,
      };
      await contentAPI.create(payload);
      toast.success('Content added!');
      setShowAddContent(false);
      setNewContent({
        title: '', description: '', content_type: 'article', path_category: 'personal',
        topic: '', content_body: '', content_url: '', duration_minutes: '', is_featured: false
      });
      loadContent();
    } catch (error) {
      toast.error('Failed to add content');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = (item) => {
    const printWindow = window.open('', '_blank');
    const body = item.content_body || '';
    // Convert markdown to simple HTML for printing
    const htmlBody = body
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
      .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
      .replace(/^---$/gm, '<hr/>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');
    
    const doc = printWindow.document;
    const sanitizedTitle = DOMPurify.sanitize(item.title);
    const sanitizedDesc = DOMPurify.sanitize(item.description || '');
    const sanitizedBody = DOMPurify.sanitize(htmlBody);
    const sanitizedExercise = item.exercise_content ? DOMPurify.sanitize(item.exercise_content.replace(/\n/g, '<br/>')) : '';
    
    doc.head.innerHTML = DOMPurify.sanitize(`<title>${sanitizedTitle} — ClarityPath</title>
      <style>
        body { font-family: Georgia, 'Times New Roman', serif; max-width: 700px; margin: 40px auto; padding: 0 20px; color: #222; line-height: 1.7; }
        h1 { font-size: 28px; margin-bottom: 8px; }
        h2 { font-size: 22px; margin-top: 32px; }
        h3 { font-size: 18px; margin-top: 24px; }
        p { margin-bottom: 16px; }
        .subtitle { color: #666; font-size: 16px; margin-bottom: 32px; }
        ul { padding-left: 24px; margin: 16px 0; }
        li { margin-bottom: 8px; }
        blockquote { border-left: 3px solid #ccc; padding-left: 16px; color: #555; font-style: italic; margin: 20px 0; }
        hr { border: none; border-top: 1px solid #ddd; margin: 32px 0; }
        .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 12px; color: #999; }
        .exercise { background: #f9f9f9; border: 1px solid #eee; padding: 20px; border-radius: 8px; margin: 24px 0; }
        .exercise-title { font-weight: bold; margin-bottom: 12px; }
        .write-lines { border-bottom: 1px solid #ccc; height: 32px; margin-bottom: 4px; }
        @media print { body { margin: 20px; } }
      </style>`);
    doc.body.innerHTML = DOMPurify.sanitize(`
      <h1>${sanitizedTitle}</h1>
      <p class="subtitle">${sanitizedDesc}</p>
      <p>${sanitizedBody}</p>
      ${item.exercise_content ? `<div class="exercise"><p class="exercise-title">Reflection Exercise</p><p>${sanitizedExercise}</p><div class="write-lines"></div><div class="write-lines"></div><div class="write-lines"></div><div class="write-lines"></div></div>` : ''}
      <div class="footer">ClarityPath — Guided by Mo</div>`);
    doc.close();
    printWindow.print();
  };

  const loadTopics = useCallback(async () => {
    try {
      const response = await contentAPI.getTopics();
      setTopics(response.data);
    } catch (error) {
      console.error('Failed to load topics:', error);
    }
  }, []);

  const loadPaths = useCallback(async () => {
    try {
      const response = await contentAPI.getPaths();
      setPaths(response.data);
    } catch (error) {
      console.error('Failed to load paths:', error);
    }
  }, []);

  const loadContent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await contentAPI.getAll(selectedTopic, selectedType, selectedPath);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content library');
    } finally {
      setLoading(false);
    }
  }, [selectedTopic, selectedType, selectedPath]);

  const handleContentClick = useCallback(async (item) => {
    if (!item.has_access) {
      setShowUpgradeDialog(true);
      return;
    }
    
    try {
      const response = await contentAPI.getById(item.id);
      setSelectedContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    }
  }, []);

  useEffect(() => {
    loadTopics();
    loadPaths();
    loadContent();
  }, [loadTopics, loadPaths, loadContent]);

  // Auto-open content from dashboard recommendation
  useEffect(() => {
    const openContentId = location.state?.openContent;
    if (openContentId && content.length > 0 && !selectedContent) {
      const item = content.find(c => c.id === openContentId);
      if (item) {
        handleContentClick(item);
        // Clear the state so it doesn't re-open on re-render
        navigate(location.pathname + location.search, { replace: true, state: {} });
      }
    }
  }, [content, location.state, selectedContent, handleContentClick, navigate, location.pathname, location.search]);

  // Group content by path_category
  const contentByPath = content.reduce((acc, item) => {
    const pathCategory = item.path_category || 'personal';
    
    if (!acc[pathCategory]) acc[pathCategory] = [];
    acc[pathCategory].push(item);
    return acc;
  }, {});

  const hasAccess = user?.subscription_tier === 'guided_path' || user?.subscription_tier === 'personal_path';
  const isFiltering = selectedTopic || selectedType || selectedPath;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="content-library-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif mb-2">Content Library</h1>
            <p className="text-muted-foreground">
              Resources to support your journey — articles, worksheets, audio, and more.
            </p>
          </div>
          {user?.is_admin && (
            <Button
              onClick={() => setShowAddContent(true)}
              className="rounded-full"
              data-testid="add-content-btn"
            >
              <Plus size={16} className="mr-2" /> Add Content
            </Button>
          )}
        </div>

        {/* Access Banner for non-subscribers */}
        {!hasAccess && (
          <Card className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock className="text-primary" size={20} />
                <div>
                  <p className="font-medium">Unlock the full library</p>
                  <p className="text-sm text-muted-foreground">
                    Upgrade to Guided Path or Personal Path for full access to all content.
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/pricing')} className="rounded-full">
                View Plans
              </Button>
            </div>
          </Card>
        )}

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Topic Filter Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Filter by topic:</span>
            <select
              value={selectedTopic || ''}
              onChange={(e) => {
                const value = e.target.value || null;
                setSelectedTopic(value);
                const params = new URLSearchParams(searchParams);
                if (value) {
                  params.set('topic', value);
                } else {
                  params.delete('topic');
                }
                setSearchParams(params);
              }}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm min-w-[200px]"
              data-testid="topic-filter"
            >
              <option value="">All Topics</option>
              {Object.entries(topics).map(([key, topic]) => (
                <option key={key} value={key}>
                  {typeof topic === 'object' ? topic.name : key.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Filter by type:</span>
            <select
              value={selectedType || ''}
              onChange={(e) => {
                const value = e.target.value || null;
                setSelectedType(value);
                const params = new URLSearchParams(searchParams);
                if (value) {
                  params.set('type', value);
                } else {
                  params.delete('type');
                }
                setSearchParams(params);
              }}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm min-w-[150px]"
              data-testid="type-filter"
            >
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {isFiltering && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTopic(null);
                setSelectedType(null);
                setSelectedPath(null);
                setSearchParams({});
              }}
              className="text-muted-foreground"
            >
              <X size={14} className="mr-1" /> Clear filters
            </Button>
          )}
        </div>

        {/* Content Display */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
            <p className="text-muted-foreground mt-4">Loading content...</p>
          </div>
        ) : isFiltering ? (
          // Filtered View - Show content in grid
          <div className="space-y-6">
            {selectedPath && PATH_INFO[selectedPath] && (
              <div className={`p-4 rounded-2xl bg-gradient-to-r ${PATH_INFO[selectedPath].color}`}>
                <div className="flex items-center gap-3">
                  {(() => { const Icon = PATH_INFO[selectedPath].icon; return <Icon size={20} />; })()}
                  <h2 className="text-lg font-serif">{PATH_INFO[selectedPath].name} — All Content</h2>
                </div>
              </div>
            )}
            {content.length === 0 ? (
              <Card className="p-8 text-center">
                <BookOpen className="mx-auto mb-4 text-muted-foreground opacity-50" size={48} />
                <p className="text-muted-foreground">No content found for this selection.</p>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {content.map((item) => (
                  <ContentCard 
                    key={item.id} 
                    item={item} 
                    onClick={() => handleContentClick(item)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          // Default View - Show by Learning Paths
          <div className="space-y-10">
            {['spiritual', 'personal', 'professional'].map((pathKey) => {
              const pathInfo = PATH_INFO[pathKey];
              const pathContent = contentByPath[pathKey] || [];
              const Icon = pathInfo.icon;
              
              // Get up to 3 items to display
              const displayItems = pathContent.slice(0, 3);
              
              if (pathContent.length === 0) {
                // Show placeholder for empty paths
                return (
                  <div key={pathKey}>
                    {/* Path Header */}
                    <div className={`p-6 rounded-2xl bg-gradient-to-r ${pathInfo.color} mb-4`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-background/80 flex items-center justify-center">
                          <Icon size={20} className="text-foreground" />
                        </div>
                        <div>
                          <h2 className="text-xl font-serif">{pathInfo.name}</h2>
                          <p className="text-sm text-muted-foreground">{pathInfo.description}</p>
                        </div>
                      </div>
                    </div>
                    <Card className="p-6 text-center border-dashed">
                      <p className="text-muted-foreground">Content coming soon...</p>
                    </Card>
                  </div>
                );
              }
              
              return (
                <div key={pathKey}>
                  {/* Path Header */}
                  <div className={`p-6 rounded-2xl bg-gradient-to-r ${pathInfo.color} mb-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-background/80 flex items-center justify-center">
                          <Icon size={20} className="text-foreground" />
                        </div>
                        <div>
                          <h2 className="text-xl font-serif">{pathInfo.name}</h2>
                          <p className="text-sm text-muted-foreground">{pathInfo.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user?.is_admin && reorderingPath !== pathKey && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startReorder(pathKey)}
                            className="text-foreground"
                            data-testid={`reorder-btn-${pathKey}`}
                          >
                            <GripVertical size={16} className="mr-1" /> Reorder
                          </Button>
                        )}
                        {pathContent.length > 3 && reorderingPath !== pathKey && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedPath(pathKey);
                              setSelectedTopic(null);
                              setSelectedType(null);
                              setSearchParams({ path: pathKey });
                            }}
                            className="text-foreground"
                          >
                            View all <ChevronRight size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Reorder Mode */}
                  {reorderingPath === pathKey ? (
                    <div className="space-y-3" data-testid={`reorder-mode-${pathKey}`}>
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={reorderItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                          <div className="space-y-2">
                            {reorderItems.map((item) => (
                              <SortableContentCard key={item.id} item={item} />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      <div className="flex gap-3 pt-2">
                        <Button
                          onClick={saveReorder}
                          disabled={reorderSaving}
                          className="rounded-full"
                          data-testid="save-reorder-btn"
                        >
                          {reorderSaving ? 'Saving...' : 'Save Order'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setReorderingPath(null); setReorderItems([]); }}
                          className="rounded-full"
                          data-testid="cancel-reorder-btn"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                  /* Content Cards */
                  displayItems.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {displayItems.map((item) => (
                        <ContentCard 
                          key={item.id} 
                          item={item} 
                          onClick={() => handleContentClick(item)}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card className="p-6 text-center border-dashed">
                      <p className="text-muted-foreground">Content coming soon...</p>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Content Viewer Modal */}
        <Dialog open={!!selectedContent} onOpenChange={(open) => { if (!open) handleCloseContent(); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {selectedContent && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {TYPE_ICONS[selectedContent.content_type] && (
                        <>
                          {(() => {
                            const Icon = TYPE_ICONS[selectedContent.content_type];
                            return <Icon size={14} />;
                          })()}
                          <span className="capitalize">{TYPE_LABELS[selectedContent.content_type] || selectedContent.content_type}</span>
                        </>
                      )}
                      {selectedContent.duration_minutes && (
                        <>
                          <span>•</span>
                          <Clock size={14} />
                          <span>{selectedContent.duration_minutes} min</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleListen(selectedContent.id)}
                        disabled={audioLoading}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="listen-btn"
                      >
                        {audioLoading ? (
                          <><Loader2 size={14} className="mr-1 animate-spin" /> Generating...</>
                        ) : isPlaying ? (
                          <><Pause size={14} className="mr-1" /> Pause</>
                        ) : audioUrl ? (
                          <><Play size={14} className="mr-1" /> Resume</>
                        ) : (
                          <><Volume2 size={14} className="mr-1" /> Listen</>
                        )}
                      </Button>
                      {audioUrl && (
                        <select
                          value={playbackSpeed}
                          onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                          className="px-1.5 py-1 rounded border border-border bg-background text-xs font-medium cursor-pointer"
                          data-testid="speed-control"
                        >
                          {SPEED_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s === 1 ? '1x' : `${s}x`}</option>
                          ))}
                        </select>
                      )}
                      {user?.is_admin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          handleCloseContent();
                          setEditingContent(selectedContent);
                        }}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="edit-content-btn"
                      >
                        <Pencil size={14} className="mr-1" />
                        Edit
                      </Button>
                    )}
                    {selectedContent.content_body && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePrint(selectedContent)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="print-btn"
                      >
                        <Printer size={14} className="mr-1" />
                        Print
                      </Button>
                    )}
                    </div>
                  </div>
                  <DialogTitle className="text-2xl font-serif">{selectedContent.title}</DialogTitle>
                  <p className="text-muted-foreground">{selectedContent.description}</p>
                </DialogHeader>
                <div className="mt-6 space-y-4">
                  {/* Video Embed */}
                  {selectedContent.content_type === 'video' && selectedContent.content_url && getYouTubeEmbedUrl(selectedContent.content_url) && (
                    <div className="aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe
                        src={getYouTubeEmbedUrl(selectedContent.content_url)}
                        title={selectedContent.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  {/* Content Body */}
                  {selectedContent.content_body ? (
                    <div className="prose prose-base max-w-none prose-headings:font-serif prose-headings:mt-8 prose-headings:mb-4 prose-p:mb-5 prose-p:leading-relaxed prose-li:my-1.5 prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6 prose-strong:text-foreground prose-h2:text-xl prose-h3:text-lg"><ReactMarkdown>{selectedContent.content_body}</ReactMarkdown></div>
                  ) : selectedContent.content_url && !getYouTubeEmbedUrl(selectedContent.content_url) ? (
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <a 
                        href={selectedContent.content_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Open content →
                      </a>
                    </div>
                  ) : !selectedContent.content_url ? (
                    <p className="text-muted-foreground italic">Content not available.</p>
                  ) : null}
                </div>

                {/* Assessment Section */}
                {selectedContent.has_assessment && selectedContent.assessment_questions?.length > 0 && (
                  <AssessmentWidget 
                    contentId={selectedContent.id}
                    questions={selectedContent.assessment_questions}
                    assessmentType={selectedContent.assessment_type}
                    giftTypes={selectedContent.gift_types}
                  />
                )}

                {/* Mark Complete */}
                <div className="border-t border-border pt-6 mt-6">
                  <Button
                    variant={selectedContent.completed ? "outline" : "default"}
                    onClick={() => handleMarkComplete(selectedContent.id, selectedContent.completed)}
                    disabled={markingComplete}
                    className="rounded-full w-full"
                    data-testid="mark-complete-btn"
                  >
                    {markingComplete ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" /> Updating...</>
                    ) : selectedContent.completed ? (
                      <><CheckCircle2 size={16} className="mr-2 text-green-600" /> Completed — Mark as Incomplete</>
                    ) : (
                      <><CheckCircle2 size={16} className="mr-2" /> Mark as Complete</>
                    )}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Upgrade Dialog */}
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock size={20} />
                Premium Content
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              This content is available to Guided Path and Personal Path members. 
              Upgrade your subscription to unlock all content.
            </p>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
                Maybe later
              </Button>
              <Button onClick={() => navigate('/pricing')}>
                View Plans
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Content Dialog */}
        <Dialog open={!!editingContent} onOpenChange={(open) => { if (!open) setEditingContent(null); }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            {editingContent && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Pencil size={18} />
                    Edit Content
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Title</label>
                    <input
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                      value={editingContent.title || ''}
                      onChange={(e) => setEditingContent({...editingContent, title: e.target.value})}
                      data-testid="edit-title"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Description</label>
                    <input
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                      value={editingContent.description || ''}
                      onChange={(e) => setEditingContent({...editingContent, description: e.target.value})}
                      data-testid="edit-description"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Content Type</label>
                    <select
                      className="px-3 py-2 border border-border rounded-lg bg-background text-sm"
                      value={editingContent.content_type || 'article'}
                      onChange={(e) => setEditingContent({...editingContent, content_type: e.target.value})}
                      data-testid="edit-content-type"
                    >
                      {Object.entries(TYPE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Learning Path</label>
                    <select
                      className="px-3 py-2 border border-border rounded-lg bg-background text-sm"
                      value={editingContent.path_category || 'personal'}
                      onChange={(e) => setEditingContent({...editingContent, path_category: e.target.value})}
                      data-testid="edit-path-category"
                    >
                      {Object.entries(PATH_INFO).map(([key, info]) => (
                        <option key={key} value={key}>{info.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium">Featured</label>
                    <button
                      type="button"
                      onClick={() => setEditingContent({...editingContent, is_featured: !editingContent.is_featured})}
                      className={`relative w-10 h-5 rounded-full transition-colors ${editingContent.is_featured ? 'bg-accent' : 'bg-muted'}`}
                      data-testid="edit-featured-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${editingContent.is_featured ? 'translate-x-5' : ''}`} />
                    </button>
                    <span className="text-xs text-muted-foreground">{editingContent.is_featured ? 'Shown as Featured' : 'Not featured'}</span>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Content Body (Markdown)</label>
                    <details className="mb-2 text-xs text-muted-foreground">
                      <summary className="cursor-pointer hover:text-foreground">Formatting Guide</summary>
                      <div className="mt-2 p-3 bg-muted rounded-lg space-y-1 font-mono">
                        <p><span className="text-foreground"># Heading</span> — Main heading</p>
                        <p><span className="text-foreground">## Subheading</span> — Section heading</p>
                        <p><span className="text-foreground">**bold text**</span> — <strong>Bold</strong></p>
                        <p><span className="text-foreground">*italic text*</span> — <em>Italic</em></p>
                        <p><span className="text-foreground">- item</span> — Bullet list</p>
                        <p><span className="text-foreground">1. item</span> — Numbered list</p>
                        <p><span className="text-foreground">&gt; quote</span> — Callout / quote</p>
                        <p><span className="text-foreground">---</span> — Horizontal line</p>
                      </div>
                    </details>
                    <textarea
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm font-mono min-h-[300px]"
                      value={editingContent.content_body || ''}
                      onChange={(e) => setEditingContent({...editingContent, content_body: e.target.value})}
                      data-testid="edit-content-body"
                    />
                  </div>
                  {editingContent.exercise_content !== undefined && (
                    <div>
                      <label className="text-sm font-medium mb-1 block">Exercise Content</label>
                      <textarea
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm min-h-[100px]"
                        value={editingContent.exercise_content || ''}
                        onChange={(e) => setEditingContent({...editingContent, exercise_content: e.target.value})}
                        data-testid="edit-exercise"
                      />
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={async () => {
                        setSaving(true);
                        try {
                          await contentAPI.updateContent(editingContent.id, {
                            title: editingContent.title,
                            description: editingContent.description,
                            content_type: editingContent.content_type,
                            path_category: editingContent.path_category,
                            content_body: editingContent.content_body,
                            exercise_content: editingContent.exercise_content,
                            is_featured: editingContent.is_featured
                          });
                          toast.success('Content updated');
                          setEditingContent(null);
                          loadContent();
                        } catch (error) {
                          toast.error('Failed to save changes');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                      className="rounded-full"
                      data-testid="save-edit-btn"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="outline" onClick={() => setEditingContent(null)} className="rounded-full">
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Content Dialog */}
        <Dialog open={showAddContent} onOpenChange={(open) => !open && setShowAddContent(false)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif">Add New Content</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Title *</label>
                <input
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                  value={newContent.title}
                  onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                  placeholder="e.g., Finding Your Voice in the Workplace"
                  data-testid="add-content-title"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description *</label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm resize-none"
                  rows={2}
                  value={newContent.description}
                  onChange={(e) => setNewContent({...newContent, description: e.target.value})}
                  placeholder="Brief summary of the content"
                  data-testid="add-content-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Content Type</label>
                  <select
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                    value={newContent.content_type}
                    onChange={(e) => setNewContent({...newContent, content_type: e.target.value})}
                    data-testid="add-content-type"
                  >
                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Learning Path</label>
                  <select
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                    value={newContent.path_category}
                    onChange={(e) => setNewContent({...newContent, path_category: e.target.value})}
                    data-testid="add-content-path"
                  >
                    {Object.entries(PATH_INFO).map(([key, info]) => (
                      <option key={key} value={key}>{info.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Topic</label>
                  <select
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                    value={newContent.topic}
                    onChange={(e) => setNewContent({...newContent, topic: e.target.value})}
                    data-testid="add-content-topic"
                  >
                    <option value="">Select a topic...</option>
                    {Object.entries(topics)
                      .filter(([_, t]) => typeof t === 'object')
                      .map(([slug, t]) => (
                        <option key={`topic-${slug}`} value={slug}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Duration (minutes)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                    value={newContent.duration_minutes}
                    onChange={(e) => setNewContent({...newContent, duration_minutes: e.target.value})}
                    placeholder="e.g., 10"
                    data-testid="add-content-duration"
                  />
                </div>
              </div>
              {newContent.content_type === 'video' && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Video URL (YouTube or Vimeo)</label>
                  <input
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
                    value={newContent.content_url}
                    onChange={(e) => setNewContent({...newContent, content_url: e.target.value})}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="add-content-url"
                  />
                  {newContent.content_url && getYouTubeEmbedUrl(newContent.content_url) && (
                    <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe
                        src={getYouTubeEmbedUrl(newContent.content_url)}
                        title="Preview"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              )}
              {newContent.content_type !== 'video' && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Content Body (Markdown)</label>
                  <details className="mb-2 text-xs text-muted-foreground">
                    <summary className="cursor-pointer hover:text-foreground">Formatting Guide</summary>
                    <div className="mt-2 p-3 bg-muted rounded-lg space-y-1 font-mono">
                      <p><span className="text-foreground"># Heading</span> — Main heading</p>
                      <p><span className="text-foreground">## Subheading</span> — Section heading</p>
                      <p><span className="text-foreground">**bold text**</span> — <strong>Bold</strong></p>
                      <p><span className="text-foreground">*italic text*</span> — <em>Italic</em></p>
                      <p><span className="text-foreground">- item</span> — Bullet list</p>
                      <p><span className="text-foreground">1. item</span> — Numbered list</p>
                      <p><span className="text-foreground">&gt; quote</span> — Callout / quote</p>
                      <p><span className="text-foreground">---</span> — Horizontal line</p>
                    </div>
                  </details>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm font-mono resize-none"
                    rows={8}
                    value={newContent.content_body}
                    onChange={(e) => setNewContent({...newContent, content_body: e.target.value})}
                    placeholder="Write content in markdown format..."
                    data-testid="add-content-body"
                  />
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Featured</label>
                <button
                  type="button"
                  onClick={() => setNewContent({...newContent, is_featured: !newContent.is_featured})}
                  className={`relative w-10 h-5 rounded-full transition-colors ${newContent.is_featured ? 'bg-accent' : 'bg-muted'}`}
                  data-testid="add-content-featured"
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${newContent.is_featured ? 'translate-x-5' : ''}`} />
                </button>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleAddContent}
                  disabled={saving}
                  className="rounded-full"
                  data-testid="save-add-content-btn"
                >
                  {saving ? 'Adding...' : 'Add Content'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddContent(false)} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// Sortable Content Card for drag-and-drop reordering
function SortableContentCard({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const TypeIcon = TYPE_ICONS[item.content_type] || FileText;
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-card-${item.id}`}>
      <Card className={`p-3 rounded-xl flex items-center gap-3 ${isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}>
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1" data-testid={`drag-handle-${item.id}`}>
          <GripVertical size={18} />
        </button>
        <TypeIcon size={16} className="text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{item.title}</p>
          <p className="text-xs text-muted-foreground truncate">{item.description}</p>
        </div>
        {item.is_featured && (
          <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full flex-shrink-0">Featured</span>
        )}
      </Card>
    </div>
  );
}

// Content Card Component
function ContentCard({ item, onClick }) {
  const TypeIcon = TYPE_ICONS[item.content_type] || FileText;
  
  return (
    <Card 
      className={`p-4 rounded-xl cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg ${
        !item.has_access ? 'opacity-75' : ''
      } ${item.completed ? 'ring-1 ring-green-200' : ''}`}
      onClick={onClick}
      data-testid={`content-card-${item.id}`}
    >
      {/* Type Badge & Lock */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <TypeIcon size={14} />
          <span className="capitalize">{TYPE_LABELS[item.content_type] || item.content_type}</span>
        </div>
        <div className="flex items-center gap-2">
          {item.completed && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`completed-badge-${item.id}`}>
              <CheckCircle2 size={12} /> Done
            </span>
          )}
          {item.is_featured && (
            <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">Featured</span>
          )}
          {!item.has_access && <Lock size={14} className="text-muted-foreground" />}
        </div>
      </div>
      
      {/* Title & Description */}
      <h3 className="font-medium mb-2 line-clamp-2">{item.title}</h3>
      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{item.description}</p>
      
      {/* Duration */}
      {item.duration_minutes && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock size={12} />
          <span>{item.duration_minutes} min</span>
        </div>
      )}
    </Card>
  );
}
